import { Attachment } from '../attachment/attachment.types';
import { OfferKind, OfferStatus } from '../offer/offer.enums';
import { Visibility } from '../visibility/visibility.types';

export type AutomatedActionOffer = {
  id: number;
  kind: OfferKind;
  location: string;
  slug: string;
  status: OfferStatus;
  title: string;
};

export type AutomatedAction =
  | {
      kind: AutomatedActionKind.SendEmail;
      data: {
        from: number | null;
        fromKind: EmailSenderKindEnum;
        subject: string;
        bodyHtml: string;
        private: boolean;
        adminIds: number[];
        signature: boolean;
        visibility: Visibility;
        sendAfterAtHour?: number | null;
        sendAfterAtMinute?: number | null;
        sendAfterDays?: number | null;
        sendAtTimezone?: string | null;
        attachmentIds: number[];
      };
      id?: number;
      canceled?: boolean;
      references?: { attachments: Attachment[] };
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddNote;
      data: {
        body: string;
        bodyJson: object | null;
        bodyHtml: string;
        private?: boolean;
        adminIds?: number[];
        visibility: Visibility;
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddFollowers;
      data: {
        adminIds: number[];
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddTask;
      data:
        | {
            adminIds: number[];
            dueDateType: 'relative' | null;
            dueDateUnit: string | null;
            dueDateValue: string | null;
            title: string;
            timezone: string | null;
          }
        | {
            adminIds: number[];
            dueDateType: 'absolute' | null;
            dueDateValue: string | null;
            title: string;
            timezone: string;
          };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AddTags;
      data: {
        tags: string[];
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.EvaluationRequest;
      data: {
        interviewTemplateId: number | null;
        adminIds: number[];
      };
      id?: number;
      canceled?: boolean;
      _destroy?: boolean;
    }
  | {
      kind: AutomatedActionKind.AssignTo;
      data: {
        offerIds: number[];
        talentPoolIds: number[];
      };
      id?: number;
      canceled?: boolean;
      references?: { offers: AutomatedActionOffer[]; talentPools: AutomatedActionOffer[] };
      _destroy?: boolean;
    };

export enum AutomatedActionKind {
  SendEmail = 'send_email',
  AddTags = 'add_tags',
  AddNote = 'add_note',
  AddTask = 'add_task',
  EvaluationRequest = 'request_evaluation',
  AddFollowers = 'add_followers',
  AssignTo = 'assign',
}

export interface AutomatedActionCandidate {
  name: string;
  emails: string[];
  id: number;
  photoThumbUrl: string;
  initials: string;
  hasAvatar: boolean;
}

export type Action = {
  actionTemplate: AutomatedAction;
  completedAt: string;
  errors: string[];
  id: number;
  trigger_action_set_id: number;
  canceled: boolean;
};

export type AutomatedActionStage = {
  id: number;
  name: string;
  position: number;
};

export type ActionSet = {
  actions: Action[];
  adminId: number;
  candidate?: AutomatedActionCandidate;
  candidateId: number;
  companyId: number;
  disqualifyReasonId: number;
  id: number;
  offerId: number;
  previousStageId: number;
  previousStage: AutomatedActionStage;
  stageId: number;
  stage: AutomatedActionStage;
  status: ActionSetStatusEnum;
};

export enum ActionSetStatusEnum {
  Completed = 'completed',
  Created = 'created',
  Canceled = 'canceled',
}

export enum EmailSenderKindEnum {
  UserPerformingAction = 'admin_performing_action',
  Recruiter = 'recruiter',
  HiringManager = 'hiring_manager',
  Admin = 'admin',
  SharedProvider = 'shared_email_provider',
}

export type EmailSenderKind = EmailSenderKindEnum | null;
